import { useCallback, useMemo } from 'react';
import { usePermissions } from '../../hooks/api';
import type { ResourceType, Action, Permission } from '../../../typings/permissions';

type BasePermissionsKey = `${ResourceType}|${Action}`;
type PermissionsKey =
  | BasePermissionsKey
  | `${BasePermissionsKey}|${string}`
  | `${BasePermissionsKey}|${string}|${string}`;
type PermissionsMap = Map<PermissionsKey, boolean>;

/**
 * Takes a permission and constructs a lookup key based on the permissions object.
 *
 * @param {Permission} permission - A given permission.
 */
const getPermissionKey = (permission: Permission): PermissionsKey => {
  let key = `${permission.resourceType}|${permission.action}`;
  if (permission.projectId) {
    key += `|${permission.projectId}`;
  }
  if (permission.releaseGroupId) {
    key += `|${permission.releaseGroupId}`;
  }
  if (permission.teamId) {
    key += `|${permission.teamId}`;
  }
  return key as PermissionsKey;
};

/**
 * Hook which will auto-fetch the user permissions (once per minute), and then
 * store the results in a Map so that we can quickly look them up. This hook
 * returns a `hasPermission` function which can be called with a given permission
 * to check if the user has that permission.
 *
 * @example // In a component
 * const MyComponent = () => {
 *   const hasPermission = useHasPermission();
 *   const canViewSettings = hasPermission({ action: Action.View, resourceType: ResourceType.Settings });
 *   if (!canViewSettings) {
 *     return null;
 *   }
 *   return <Settings />;
 * };
 *
 * @example // In a custom hook
 * const useCanViewSettings = () => {
 *   const hasPermission = useHasPermission();
 *   return hasPermission({ action: Action.View, resourceType: ResourceType.Settings });
 * };
 */
const useHasPermission = () => {
  const { data: permissions } = usePermissions();

  //  Store the permissions in a Map for quick lookups.
  //  Note that this will be improved/merged with our existing PermissionsCacheManager.serializePermissions
  const permissionsMap = useMemo<PermissionsMap>(() => {
    if (!permissions) {
      return new Map();
    }
    return new Map(permissions.map((permission) => [getPermissionKey(permission), true]));
  }, [permissions]);

  //  Function to actually look up the user permissions.
  const hasPermission = useCallback((permission: Permission): boolean =>
    permissionsMap.get(getPermissionKey(permission)) ?? false, [permissionsMap]);

  return hasPermission;
};

export default useHasPermission;
